import {
  isMediaCardString,
  keyToMediaCardType,
  type MediaCardMetadata,
  type MediaCardType,
  type MediaString,
} from '@wandb/weave/common/types/media';
import {clamp} from 'lodash';
import {type RefObject, useMemo} from 'react';

import {type ClassLabelMap} from '../../util/media';
import {type RunWithHistoryAndMediaWandb} from '../MediaCard';
import {type KeyTypes} from './common';
import {
  type Layout,
  type MediaBrowserPanelProps,
  type MediaConfigWithDefaultsAndMediaKeys,
  type SizingSettings,
} from './Component';
import * as PANEL_MEDIA_BROWSER_CONFIG from './config';
import {useAllRunsWithColors} from './hooks/useAllRunsWithColors';
import {useLayoutGeneration} from './hooks/useLayoutGeneration';
import {useMediaKeys} from './hooks/useMediaKeys';
import {useMediaSliderKey} from './hooks/useMediaSliderKey';
import {useRepresentativeMediaMetadata} from './hooks/useRepresentativeMediaMetadata';
import {useRunsWithMedia} from './hooks/useRunsWithMedia';
import {computeStepMeta} from './hooks/useStepMeta';
import {useTilePagination} from './hooks/useTilePagination';
import {getColumnCountFromConfig} from './layout/renderers/rendererUtils';
import {getModeConfig} from './modes/getModeConfig';
import {type TilePagination} from './tiles';
import {type StepMeta} from './types';
import {useMediaCardTypes} from './useGetMediaCardTypes';
import {useConfigWithDefaults} from './useMediaPanelConfig';
import {resolveCurrentStep} from './utils/stepUtils';
import {
  type PanelBuildMetrics,
  recordStepMetaBuildTiming,
} from './utils/tracking';

export {
  getConfigWithDefaults,
  useAllRuns,
  useConfigWithDefaults,
  useOverlays,
} from './useMediaPanelConfig';

type MediaPanelValues = {
  mediaType: MediaCardType | null;
  hasBoxes: boolean;
  hasMasks: boolean;
  layout: Layout;
  tilePagination: TilePagination | null;
  mediaKeys: string[] | undefined;
  numericKeys: string[] | undefined;
  defaultKeys: string[] | undefined;
  allMediaKeys: string[];
  keyTypes: KeyTypes;
  mediaString: MediaString | null;
  flattenedMediaMetadataForAllRuns: MediaCardMetadata[];
  firstMediaMetadata: MediaCardMetadata | null;
  currentStep: number;
  stepMeta: StepMeta;
  allMasks: ClassLabelMap;
  allBoxes: ClassLabelMap;
  configWithDefaultsAndMediaKeys: MediaConfigWithDefaultsAndMediaKeys;
  sizingSettings: SizingSettings;
  snapToExistingStep: boolean;
  runsWithMedia: RunWithHistoryAndMediaWandb[];
  allRuns: RunWithHistoryAndMediaWandb[];
};

function dedupeRunsByIdentity(
  runs: RunWithHistoryAndMediaWandb[]
): RunWithHistoryAndMediaWandb[] {
  const seenRuns = new Set<string>();
  const dedupedRuns = runs.filter(run => {
    const runKey = `${run.entityName}/${run.projectName}/${run.name}`;
    if (seenRuns.has(runKey)) {
      return false;
    }
    seenRuns.add(runKey);
    return true;
  });
  return dedupedRuns;
}

/**
 * Media panel data: keys, runs, step state, layout (slots/grid), pagination, and config defaults.
 *
 * When `panelBuildMetricsRef` is provided, step-meta and layout-generation timings are
 * recorded there for tile-load instrumentation (MEDIA-1C/1D).
 */
export function useMediaPanelValues(
  props: MediaBrowserPanelProps,
  panelBuildMetricsRef?: RefObject<PanelBuildMetrics>
): MediaPanelValues {
  const {config} = props;
  const {
    mediaKeys,
    mediaSliderKey: mediaSliderKeyFromConfig,
    snapToExistingStep = true,
  } = config;

  const {allMediaKeys, defaultKeys, keyTypes, numericKeys} = useMediaKeys();

  const firstMediaKey = useMemo(() => {
    return mediaKeys?.[0] ?? null;
  }, [mediaKeys]);

  // Type resolved from the workspace's sampled history keys. Under NGW this set
  // is truncated, so a manually-configured key can be missing from it.
  const sampledMediaString = useMemo(() => {
    return firstMediaKey != null
      ? (keyTypes[firstMediaKey] as MediaString)
      : null;
  }, [firstMediaKey, keyTypes]);

  const sampledMediaType = useMediaCardTypes(mediaKeys)?.[0] ?? null;
  const mediaSliderKey = useMediaSliderKey(mediaSliderKeyFromConfig);

  const allRuns = useAllRunsWithColors({
    data: props.data,
    query: props.query,
    pageQuery: props.pageQuery,
    customRunColors: props.customRunColors,
  });
  // including required runs can trigger multi queries, and
  // if the initial query is not filtering to specific runs,
  // it's possible that the queries overlap.
  const modeAwareAllRuns = useMemo(
    () =>
      props.query.runsetQueries.length > 1
        ? dedupeRunsByIdentity(allRuns)
        : allRuns,
    [allRuns, props.query]
  );

  const shouldSkipFiltering = !getModeConfig(config.mode)
    .filterToRunsWithMediaKey;

  const {runsWithMedia, runStepsMap} = useRunsWithMedia(
    modeAwareAllRuns,
    mediaKeys,
    shouldSkipFiltering
  );

  const stepMeta = useMemo(() => {
    // oxlint-disable-next-line react/purity
    const startPerformanceMs = performance.now();
    const result = computeStepMeta(
      runsWithMedia,
      mediaSliderKey,
      runStepsMap,
      mediaKeys
    );
    if (panelBuildMetricsRef && result.runs.length > 0) {
      recordStepMetaBuildTiming(panelBuildMetricsRef, {
        startPerformanceMs,
        // oxlint-disable-next-line react/purity
        durationMs: performance.now() - startPerformanceMs,
      });
    }
    return result;
  }, [
    runsWithMedia,
    mediaSliderKey,
    runStepsMap,
    mediaKeys,
    panelBuildMetricsRef,
  ]);

  const currentStep = resolveCurrentStep(
    config.stepIndex,
    stepMeta,
    mediaSliderKey
  );

  const firstMediaMetadata = useRepresentativeMediaMetadata(
    runsWithMedia,
    mediaKeys,
    currentStep,
    {
      mode: config.mode,
      compareSettings: config.compareSettings,
    }
  );

  // When the sampled key set doesn't cover this panel's key (NGW truncation),
  // resolve the media type from the panel's own fetched data instead, so a
  // manual panel with a key outside the sample still renders its media rather
  // than the "select runs that logged ..." empty state.
  const mediaStringFromData =
    (firstMediaMetadata?._type as MediaString | undefined) ?? null;
  const mediaString = sampledMediaString ?? mediaStringFromData;
  const mediaType =
    sampledMediaType ??
    (mediaStringFromData != null && isMediaCardString(mediaStringFromData)
      ? keyToMediaCardType(mediaStringFromData)
      : null);

  const {configWithDefaults, allMasks, hasMasks, allBoxes, hasBoxes} =
    useConfigWithDefaults({
      data: props.data,
      query: props.query,
      config: props.config,
      customRunColors: props.customRunColors,
      pageQuery: props.pageQuery,
    });

  const configWithDefaultsAndMediaKeys = useMemo(() => {
    return {
      ...configWithDefaults,
      mediaKeys: mediaKeys ?? [],
    };
  }, [configWithDefaults, mediaKeys]);

  const sizingSettings = useMemo(() => {
    const columnCount = getColumnCountFromConfig(
      configWithDefaultsAndMediaKeys
    );
    const {min, max} =
      configWithDefaultsAndMediaKeys.mode === 'compare'
        ? {
            min: PANEL_MEDIA_BROWSER_CONFIG.MIN_COMPARE_COL_COUNT,
            max: PANEL_MEDIA_BROWSER_CONFIG.MAX_COMPARE_COL_COUNT,
          }
        : {
            min: PANEL_MEDIA_BROWSER_CONFIG.MIN_COL_COUNT,
            max: PANEL_MEDIA_BROWSER_CONFIG.MAX_COL_COUNT,
          };
    return {
      columnCount: {
        min,
        max,
        current: clamp(columnCount, min, max),
      },
    };
  }, [configWithDefaultsAndMediaKeys]);

  const layout = useLayoutGeneration(
    {
      runsWithMedia: configWithDefaultsAndMediaKeys ? runsWithMedia : [],
      stepMeta,
      allMasks,
      configWithDefaults: configWithDefaultsAndMediaKeys,
      mediaType,
    },
    panelBuildMetricsRef
  );

  const tilePagination = useTilePagination(
    layout,
    configWithDefaultsAndMediaKeys,
    mediaType
  );

  const flattenedMediaMetadataForAllRuns = useMemo(() => {
    const result: MediaCardMetadata[] = [];
    const seenKeys = new Set<string>();

    for (const r of runsWithMedia) {
      if (!r.history) {
        continue;
      }

      for (const h of r.history) {
        if (!mediaKeys) {
          continue;
        }

        for (const k of mediaKeys) {
          const metadata = h[k];
          if (metadata == null) {
            continue;
          }

          const uniqueKey = `${r.name}-${h._step}-${k}`;
          if (seenKeys.has(uniqueKey)) {
            continue;
          }

          seenKeys.add(uniqueKey);
          result.push(metadata);
        }
      }
    }
    return result;
  }, [runsWithMedia, mediaKeys]);

  return {
    mediaType,
    mediaString,
    mediaKeys,
    allMediaKeys,
    keyTypes,
    numericKeys,
    defaultKeys,

    runsWithMedia,
    allRuns: modeAwareAllRuns,
    flattenedMediaMetadataForAllRuns,

    stepMeta,
    currentStep,
    firstMediaMetadata,

    layout,

    allMasks,
    hasMasks,
    allBoxes,
    hasBoxes,

    configWithDefaultsAndMediaKeys,
    snapToExistingStep,

    sizingSettings,
    tilePagination,
  };
}
